import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/useDebounce';
import type { AsyncCommandProvider, CommandResult } from '../types';

export function useAsyncProviderSearch(
  provider: AsyncCommandProvider,
  query: string,
  options: { enabled: boolean; maxResults: number; isScoped?: boolean }
): { results: CommandResult[]; isLoading: boolean } {
  const debouncedQuery = useDebounce(query, 200);
  const minLen = provider.minQueryLength ?? 2;
  // In scoped mode, fire immediately even with empty query to show recent items
  const shouldSearch = options.enabled && (options.isScoped || debouncedQuery.length >= minLen);

  const { data, isLoading } = useQuery({
    queryKey: ['command-palette', provider.id, debouncedQuery],
    queryFn: ({ signal }) => provider.search(debouncedQuery, { signal }),
    enabled: shouldSearch,
    staleTime: 30_000,
    gcTime: 60_000,
    placeholderData: keepPreviousData,
  });

  return {
    results: (data ?? []).slice(0, options.maxResults),
    // Show loading when first fetching (no cached data yet)
    isLoading: shouldSearch && isLoading,
  };
}
